import React, { useState, useEffect } from 'react';
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { CircularProgress, Box } from '@material-ui/core';
import baseTheme, { modernTheme, modernDarkTheme } from '../theme';
import './brand/brand.css';   // fontes da marca ecooGPS (Inter + JetBrains Mono)
import AdminLogin from './pages/AdminLogin';
import AdminLayout from './components/AdminLayout';

// Tema do backoffice — NiceAdmin verde. Deep-merge do tema global só a mudar o
// primary para verde-escuro → todos os componentes MUI (Switch, Checkbox,
// Select, botões contained) ficam verdes automaticamente.
const theme = createMuiTheme(baseTheme, {
  palette: {
    primary: { main: '#2f5d50', dark: '#274c41', light: '#3d7565', contrastText: '#ffffff' },
    secondary: { main: '#5a8577', contrastText: '#ffffff' },
  },
});

// Tema alternativo "FuelCheck" (opt-in por toggle, guardado em localStorage por
// quem está a ver). Reutiliza o modernTheme (paleta clara + azul limpo).
const THEME_KEY = 'admin_ui_theme';

const API_BASE = '/planner-api';

function AdminApp() {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // 3 estados: 'classic' (verde legado), 'fuelcheck' (identidade ecooGPS clara),
  // 'fuelcheck-dark' (escura). DEFAULT = nova identidade ecooGPS (rebrand); o
  // Clássico mantém-se no toggle durante a transição. Só respeita o Clássico se
  // foi escolhido explicitamente.
  const [uiTheme, setUiTheme] = useState(() => {
    try {
      const v = localStorage.getItem(THEME_KEY);
      return (v === 'classic' || v === 'fuelcheck' || v === 'fuelcheck-dark') ? v : 'fuelcheck';
    } catch { return 'fuelcheck'; }
  });
  const activeTheme = uiTheme === 'fuelcheck-dark' ? modernDarkTheme : uiTheme === 'fuelcheck' ? modernTheme : theme;
  function pickTheme(next) {
    setUiTheme(() => {
      try { localStorage.setItem(THEME_KEY, next); } catch { /* ignora */ }
      return next;
    });
  }
  // Marca o <html> com o tema → as variáveis CSS --adm-* cascateiam para TUDO,
  // incluindo diálogos renderizados em portal (fora da árvore do AdminLayout).
  useEffect(() => {
    const el = document.documentElement;
    el.setAttribute('data-admin-theme', uiTheme);
    return () => { el.removeAttribute('data-admin-theme'); };
  }, [uiTheme]);

  useEffect(() => {
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, [token]);

  async function verifyToken(t) {
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.role !== 'super_admin') {
          handleLogout();
          return;
        }
        setUser(data);
      } else {
        handleLogout();
      }
    } catch {
      handleLogout();
    }
    setLoading(false);
  }

  async function handleLogin(email, password) {
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { ok: false, error: data.error || 'Credenciais invalidas' };
      }
      if (data.user.role !== 'super_admin') {
        return { ok: false, error: 'Acesso restrito a super administradores' };
      }
      localStorage.setItem('admin_token', data.token);
      setToken(data.token);
      setUser(data.user);
      return { ok: true };
    } catch {
      return { ok: false, error: 'Erro de ligacao ao servidor' };
    }
  }

  function handleLogout() {
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
    setLoading(false);
  }

  if (loading) {
    return (
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <AdminLogin onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <AdminLayout user={user} token={token} onLogout={handleLogout} uiTheme={uiTheme} onPickTheme={pickTheme} />
    </ThemeProvider>
  );
}

export default AdminApp;
